import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Lock, User, Loader2 } from "lucide-react";

// Home pubblica: relativa per default (risolve al dominio corrente, es. https://petrolgiuly.it/ in produzione),
// sovrascrivibile con REACT_APP_PUBLIC_SITE_URL per ambienti diversi. Nessun URL di preview hardcoded.
const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || "/";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to={user.must_change_password ? "/cambia-password" : "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      navigate(u.must_change_password ? "/cambia-password" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background enilive-grid-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <a
            href={PUBLIC_SITE_URL}
            aria-label="Torna alla home di Petrol Giuly"
            data-testid="login-logo-link"
            className="cursor-pointer rounded-2xl outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img src="/logo_icon.png" alt="Petrol Giuly" className="h-20 w-20 rounded-2xl" />
          </a>
          <div className="text-center">
            <h1 className="font-display text-3xl font-black tracking-tight">PETROL GIULY</h1>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">Gestione punti vendita, incassi e riconciliazioni</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8"
          data-testid="login-form"
        >
          <h2 className="mb-6 font-display text-xl font-bold">Accedi</h2>

          {error && (
            <div
              className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Username
              </Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 pl-10 text-base"
                  placeholder="admin"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 text-base"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="mt-6 h-12 w-full text-base font-bold"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entra"}
          </Button>
        </form>
      </div>
    </div>
  );
}
